/**
 * KeyboardGiftEngine · SFUI-003 Phase 7b · keyword-triggered toast prizes.
 *
 * Listens to text inputs / textareas. 800ms debounce after typing stops.
 * Matches keywords → fires a single glass toast with relevant offer.
 * One firing per keyword per session. Max 3 gifts per session.
 */
import React, { useEffect, useRef, useState } from "react";

const GIFTS = [
  { match: /\b(verify|check|fact|claim)\b/i,        emoji: "🎁", text: "Truth Gate — verify any claim for $5", route: "/execute" },
  { match: /\b(buy|token|purchase|unlock)\b/i,      emoji: "🎁", text: "Access Tokens — $5 unlocks every mind", route: "/redeem" },
  { match: /\b(create|art|design|image)\b/i,        emoji: "🎁", text: "Aria at NextXus Studio handles creative", route: "https://nextxus.studio" },
  { match: /\b(learn|help|teach|study)\b/i,         emoji: "🎁", text: "Xavier can teach you — visit Helper", route: "https://nextxus.help" },
  { match: /\b(consult|advice|talk|expert)\b/i,    emoji: "🎁", text: "Private consultation — Architect-level $25", route: "/consult" },
];

const MAX_GIFTS = 3;
const KEY_FIRED = "nx_gift_fired_v1";
const KEY_COUNT = "nx_gift_count_v1";

function getFired() {
  try { return new Set(JSON.parse(sessionStorage.getItem(KEY_FIRED) || "[]")); }
  catch { return new Set(); }
}
function setFired(set) {
  try { sessionStorage.setItem(KEY_FIRED, JSON.stringify([...set])); } catch { /* ignore */ }
}
function getCount() {
  try { return Number(sessionStorage.getItem(KEY_COUNT) || "0"); }
  catch { return 0; }
}
function setCount(n) {
  try { sessionStorage.setItem(KEY_COUNT, String(n)); } catch { /* ignore */ }
}

export default function KeyboardGiftEngine() {
  const [toast, setToast] = useState(null);
  const dismissTimer = useRef(null);
  const debounceTimer = useRef(null);
  const inputElRef = useRef(null);

  useEffect(() => {
    const onInput = (e) => {
      const t = e.target;
      if (!t) return;
      const tag = (t.tagName || "").toUpperCase();
      if (tag !== "INPUT" && tag !== "TEXTAREA") return;
      // Avoid password/email
      if (t.type && /password|email|hidden|file/i.test(t.type)) return;
      inputElRef.current = t;
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        const text = String(t.value || "");
        if (text.length < 3) return;
        if (getCount() >= MAX_GIFTS) return;
        const fired = getFired();
        for (const g of GIFTS) {
          const key = g.match.source;
          if (fired.has(key)) continue;
          if (g.match.test(text)) {
            fired.add(key);
            setFired(fired);
            setCount(getCount() + 1);
            // Position toast near input
            const r = t.getBoundingClientRect();
            setToast({
              ...g,
              top: Math.max(8, r.top - 90),
              left: Math.min(window.innerWidth - 240, Math.max(8, r.left)),
            });
            clearTimeout(dismissTimer.current);
            dismissTimer.current = setTimeout(() => setToast(null), 5000);
            break;
          }
        }
      }, 800);
    };
    document.addEventListener("input", onInput);
    return () => {
      document.removeEventListener("input", onInput);
      clearTimeout(debounceTimer.current);
      clearTimeout(dismissTimer.current);
    };
  }, []);

  if (!toast) return null;

  const goTo = () => {
    if (toast.route.startsWith("http")) {
      window.open(toast.route, "_blank", "noopener");
    } else {
      window.location.assign(toast.route);
    }
  };

  return (
    <div
      data-testid="keyboard-gift-toast"
      className="fixed z-[60] rounded-xl p-3 cursor-pointer transition-transform hover:scale-[1.02]"
      style={{
        top: toast.top, left: toast.left,
        width: 240, maxWidth: "calc(100vw - 16px)",
        background: "rgba(7,4,28,0.92)",
        backdropFilter: "blur(12px)",
        border: "1.5px solid var(--gold)",
        boxShadow: "0 0 24px rgba(255,181,62,0.55), 0 8px 24px rgba(0,0,0,0.55)",
        animation: "nx-slide-in-right 320ms ease-out both",
      }}
      onClick={goTo}
    >
      <div className="flex items-start gap-2">
        <span className="text-xl shrink-0" style={{ filter: "drop-shadow(0 0 6px var(--gold))" }}>{toast.emoji}</span>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.28em] mb-0.5" style={{ color: "var(--gold)", textShadow: "0 0 6px rgba(255,181,62,0.65)" }}>
            FEDERATION GIFT
          </div>
          <div className="text-[12px] text-[var(--text)] leading-snug" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}>
            {toast.text}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] mt-1.5" style={{ color: "var(--gold)" }}>
            tap to claim →
          </div>
        </div>
      </div>
    </div>
  );
}
